import { useMutation, useQuery } from '@apollo/client';
import IconButton from '@mui/material/IconButton';
import { useCallback, useEffect, useState } from 'react';

import { gengql } from '@/__generated__';
import { BillingInterval } from '@/__generated__/graphql';
import { money } from '@/core/money';
import { formatDate } from '@/utils';

import Dialog from '../Dialog';

const PREVIEW_SEAT_CHANGE = gengql(/* GraphQL */ `
  query previewSeatChange($subscriptionId: String!, $newQuantity: Int!) {
    previewSeatChange(subscriptionId: $subscriptionId, newQuantity: $newQuantity) {
      immediateCharge {
        cents
        currency
      }
      nextInvoiceChange {
        cents
        currency
      }
      effectiveDate
    }
  }
`);

const REACTIVATE_SUBSCRIPTION = gengql(/* GraphQL */ `
  mutation reactivateSubFromModal($subscriptionId: String!) {
    reactivateSubscription(subscriptionId: $subscriptionId) {
      id
      status
      billingInterval
      quantity
      pricePerSeat {
        cents
        currency
      }
      totalPrice {
        cents
        currency
      }
      currentPeriodEnd
      trialEnd
      isTrialing
      daysUntilTrialEnd
      daysUntilRenewal
      cancelAtPeriodEnd
      isActive
    }
  }
`);

const UPDATE_SUBSCRIPTION_SEATS = gengql(/* GraphQL */ `
  mutation updateSubscriptionSeats($subscriptionId: String!, $newQuantity: Int!) {
    updateSubscriptionSeats(subscriptionId: $subscriptionId, newQuantity: $newQuantity) {
      prorationPreview {
        cents
        currency
      }
      subscription {
        id
        status
        billingInterval
        quantity
        pricePerSeat {
          cents
          currency
        }
        totalPrice {
          cents
          currency
        }
        currentPeriodEnd
        trialEnd
        isTrialing
        daysUntilTrialEnd
        daysUntilRenewal
        cancelAtPeriodEnd
        isActive
      }
    }
  }
`);

interface ManageSeatsModalProps {
  open: boolean;
  mode?: 'manage' | 'activate';
  subscription: any;
  seatAllocation: any;
  onClose: () => void;
  onUpdate: (subscription: any) => void;
}

const seatButtonStyle = {
  border: '1px solid var(--borderColor)',
  borderRadius: 4,
  width: 36,
  height: 36,
};

const ManageSeatsModal = ({
  open,
  mode = 'manage',
  subscription,
  seatAllocation,
  onClose,
  onUpdate,
}: ManageSeatsModalProps): JSX.Element => {
  const isActivation = mode === 'activate';
  const currentQuantity = subscription.quantity;
  const seatsInUse = seatAllocation?.used ?? 0;
  const minSeats = Math.max(seatsInUse, 1);
  const isMonthly = subscription.billingInterval === BillingInterval.Month;

  const [newQuantity, setNewQuantity] = useState(currentQuantity);
  const [debouncedQuantity, setDebouncedQuantity] = useState(currentQuantity);
  const [error, setError] = useState<string | null>(null);
  const hasChanged = newQuantity !== currentQuantity;
  const isAdding = newQuantity > currentQuantity;

  useEffect(() => {
    if (open) {
      setNewQuantity(currentQuantity);
      setDebouncedQuantity(currentQuantity);
      setError(null);
    }
  }, [open, currentQuantity]);

  useEffect(() => {
    if (newQuantity === currentQuantity) return;
    const timer = setTimeout(() => setDebouncedQuantity(newQuantity), 400);
    return () => clearTimeout(timer);
  }, [newQuantity, currentQuantity]);

  const shouldPreview = debouncedQuantity !== currentQuantity;
  const { data: previewData, loading: previewLoading } = useQuery(PREVIEW_SEAT_CHANGE, {
    variables: { subscriptionId: subscription.id, newQuantity: debouncedQuantity },
    skip: !shouldPreview || !open,
  });

  const [updateSeats, { loading: updateLoading }] = useMutation(UPDATE_SUBSCRIPTION_SEATS);
  const [reactivateSub, { loading: reactivateLoading }] = useMutation(REACTIVATE_SUBSCRIPTION);

  const handleConfirm = useCallback(async () => {
    setError(null);
    try {
      if (isActivation) {
        const { data: reactivateData } = await reactivateSub({
          variables: { subscriptionId: subscription.id },
        });
        const reactivatedSub = reactivateData?.reactivateSubscription;
        if (!reactivatedSub) return;

        if (newQuantity !== currentQuantity) {
          const { data: seatData } = await updateSeats({
            variables: { subscriptionId: subscription.id, newQuantity },
          });
          if (seatData?.updateSubscriptionSeats?.subscription) {
            onUpdate(seatData.updateSubscriptionSeats.subscription);
          } else {
            onUpdate(reactivatedSub);
          }
        } else {
          onUpdate(reactivatedSub);
        }
        onClose();
        return;
      }

      const { data } = await updateSeats({
        variables: { subscriptionId: subscription.id, newQuantity },
      });
      if (data?.updateSubscriptionSeats?.subscription) {
        onUpdate(data.updateSubscriptionSeats.subscription);
        onClose();
      }
    } catch (err: any) {
      const msg = err?.graphQLErrors?.[0]?.message || err?.message || 'Something went wrong';
      setError(msg);
    }
  }, [
    subscription.id,
    newQuantity,
    currentQuantity,
    isActivation,
    updateSeats,
    reactivateSub,
    onUpdate,
    onClose,
  ]);

  const perSeat = subscription.pricePerSeat
    ? money(subscription.pricePerSeat)
    : money({ cents: 0, currency: 'USD' });
  const preview = previewData?.previewSeatChange;
  const showPreview = hasChanged && debouncedQuantity === newQuantity && preview;
  const seatDelta = Math.abs(newQuantity - currentQuantity);

  // Compute new total client-side (API's nextInvoiceChange is a delta, not absolute)
  const perSeatMonthly = isMonthly
    ? perSeat
    : money({
        cents: Math.round(perSeat.cents / 12),
        currency: subscription.pricePerSeat.currency,
      });
  const newTotalPerInterval = money({
    cents: perSeat.cents * newQuantity,
    currency: subscription.pricePerSeat.currency,
  });
  const newTotalMonthly = money({
    cents: perSeatMonthly.cents * newQuantity,
    currency: subscription.pricePerSeat.currency,
  });

  return (
    <Dialog
      open={open}
      title={isActivation ? 'Activate Subscription' : 'Manage Seats'}
      onConfirm={handleConfirm}
      onCancel={onClose}
      confirmLabel={isActivation ? 'Activate Subscription' : 'Update Seats'}
      disableSubmit={!isActivation && (!hasChanged || previewLoading)}
      submitting={updateLoading || reactivateLoading}
    >
      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 4 }}>
          <strong>Current seats</strong>
        </div>
        <div>
          {`${currentQuantity} \u00d7 ${perSeatMonthly.formatted({ omitEmptyCents: true })}/mo = ${money(subscription.totalPrice).formatted({ omitEmptyCents: true })}/${isMonthly ? 'mo' : 'yr'}`}
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 4 }}>
          <strong>Seats in use</strong>
        </div>
        <div>
          {seatsInUse} of {currentQuantity} assigned
        </div>
      </div>

      <div style={{ marginBottom: 4 }}>
        <strong>New seat count</strong>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
        <IconButton
          onClick={() => setNewQuantity((q: number) => Math.max(minSeats, q - 1))}
          disabled={newQuantity <= minSeats}
          size="small"
          style={seatButtonStyle}
        >
          &minus;
        </IconButton>
        <span style={{ fontSize: 20, fontWeight: 'bold', minWidth: 40, textAlign: 'center' }}>
          {newQuantity}
        </span>
        <IconButton
          onClick={() => setNewQuantity((q: number) => q + 1)}
          size="small"
          style={seatButtonStyle}
        >
          +
        </IconButton>
      </div>
      <div style={{ fontSize: 12, color: 'var(--darkGray)', marginBottom: 20 }}>
        Minimum 1 seat. Cannot reduce below seats in use.
      </div>

      {hasChanged && previewLoading && (
        <div style={{ fontSize: 14, color: 'var(--darkGray)' }}>Calculating...</div>
      )}
      {showPreview && isAdding && (
        <div style={{ fontSize: 14, color: 'var(--teal)' }}>
          <strong>
            Adding {seatDelta} <span>{seatDelta === 1 ? 'seat' : 'seats'}</span>
          </strong>
          <div style={{ marginTop: 8 }}>
            <strong>
              {isMonthly
                ? `New monthly total: ${newTotalPerInterval.formatted({ omitEmptyCents: true })}/mo`
                : `New annual total: ${newTotalPerInterval.formatted({ omitEmptyCents: true })}/yr (${newTotalMonthly.formatted({ omitEmptyCents: true })}/mo)`}
            </strong>
          </div>
          {preview.immediateCharge && money(preview.immediateCharge).cents > 0 && (
            <div>
              Prorated charge today ({subscription.daysUntilRenewal} days remaining):{' '}
              {money(preview.immediateCharge).formatted()}
            </div>
          )}
        </div>
      )}
      {showPreview && !isAdding && (
        <div style={{ fontSize: 14, color: 'var(--amber700, #b45309)' }}>
          <strong>
            Removing {seatDelta} <span>{seatDelta === 1 ? 'seat' : 'seats'}</span>
          </strong>
          <div>Takes effect at next billing cycle ({formatDate(preview.effectiveDate)}).</div>
          <div style={{ marginTop: 8 }}>
            <strong>
              {isMonthly
                ? `New monthly total: ${newTotalPerInterval.formatted({ omitEmptyCents: true })}/mo`
                : `New annual total: ${newTotalPerInterval.formatted({ omitEmptyCents: true })}/yr (${newTotalMonthly.formatted({ omitEmptyCents: true })}/mo)`}
            </strong>
          </div>
        </div>
      )}
      {error && (
        <div style={{ marginTop: 12, fontSize: 14, color: 'var(--red500, #E94F74)' }}>{error}</div>
      )}
    </Dialog>
  );
};

export default ManageSeatsModal;
